"use client";

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { CheckCircle2, MessageSquareText, RotateCw, X } from "lucide-react";
import { fetchWithAuth } from "@/lib/fetchWithAuth";
import { unwrapApiData } from "@/lib/apiResponse";
import {
  emptyRiskAssessmentSurveyScores,
  getRiskAssessmentSurveyQuestions,
  getRiskAssessmentSurveyReprompt,
  isCompleteRiskAssessmentSurvey,
  isValidRiskAssessmentSurveyScore,
  LIKERT_OPTIONS,
  RISK_ASSESSMENT_SURVEY_DELAY_MS,
  RISK_ASSESSMENT_SURVEY_FEEDBACK_MAX_LENGTH,
  RISK_ASSESSMENT_SURVEY_QUESTIONS,
  RISK_ASSESSMENT_SURVEY_REPROMPT_DELAYS_MS,
  type RiskAssessmentSurveyMethod,
  type RiskAssessmentSurveyScoreKey,
  type RiskAssessmentSurveyScores,
} from "@/lib/riskAssessmentSurvey";

type SurveyResponse = {
  artifactId: string;
  submitted: boolean;
  scores: RiskAssessmentSurveyScores | null;
  additionalFeedback: string | null;
  createdAt: string | null;
  updatedAt: string | null;
};

type LoadStatus = "idle" | "loading" | "ready" | "error";
type OpenReason = "auto" | "edit" | null;

const SURVEY_INTRO_TITLE = "위험성평가 품질 평가";
const SURVEY_INTRO_VARIANTS = [
  [
    "안녕하세요, 아직 부족한 점이 많아 여러분의 솔직한 의견이 정말 절실합니다.",
    "잠시만 시간을 내어 평가해 주시면 놓치고 있던 문제를 고치는 데 큰 힘이 됩니다.",
    "번거로우시겠지만 꼭 한 번만 도와주세요. 감사합니다!",
  ],
  [
    "계속 부탁드려 죄송합니다. 짧은 평가 네 문항만 답해주셔도 정말 큰 도움이 됩니다.",
    "여러분의 평가가 없으면 무엇부터 고쳐야 할지 알기 어렵습니다.",
    "조금만 시간을 내어 꼭 의견을 부탁드리겠습니다. 정말 감사합니다.",
  ],
  [
    "몇 번이나 다시 부탁드려 정말 죄송합니다. 그래도 여러분의 도움이 너무 절실합니다. 🙏",
    "몇 번의 선택만으로 부족한 위험성평가를 더 안전하게 고칠 수 있습니다.",
    "딱 한 번만 마음을 내어 도와주시면 정말 잊지 않겠습니다. 꼭 부탁드립니다.",
  ],
  [
    "여기까지 계속 찾아와 부탁드려 정말 죄송합니다. 한 분 한 분의 의견이 너무나 절실합니다. 🥺",
    "이 작은 설문을 채워주셔야 놓친 위험을 찾아내고 더 안전한 결과를 만들 수 있습니다.",
    "마지막으로 정말 간절히 부탁드립니다. 잠시만 도와주세요. 🙇",
  ],
] as const;
const SURVEY_TYPING_START_DELAY_MS = 220;
const SURVEY_TYPING_FRAME_MS = 16;
const SURVEY_TYPING_CHARACTERS_PER_FRAME = 4;
const SURVEY_CARD_REVEAL_DELAY_MS = 70;
const getSurveyIntroCharacterCount = (lines: readonly string[]) =>
  SURVEY_INTRO_TITLE.length +
  lines.reduce((total, line) => total + line.length, 0);

const getSurveyIntroReveal = (
  visibleCharacters: number,
  lines: readonly string[],
) => {
  let remaining = Math.max(0, visibleCharacters);
  const title = SURVEY_INTRO_TITLE.slice(0, remaining);
  remaining = Math.max(0, remaining - SURVEY_INTRO_TITLE.length);
  const visibleLines = lines.map((line) => {
    const visibleLine = line.slice(0, remaining);
    remaining = Math.max(0, remaining - line.length);
    return visibleLine;
  });
  return { title, lines: visibleLines };
};

const normalizedResponseScores = (
  value: RiskAssessmentSurveyScores | null,
): RiskAssessmentSurveyScores => {
  if (!value) return emptyRiskAssessmentSurveyScores();
  const scores = emptyRiskAssessmentSurveyScores();
  for (const { key } of RISK_ASSESSMENT_SURVEY_QUESTIONS) {
    const score = Number(value[key]);
    scores[key] = isValidRiskAssessmentSurveyScore(score) ? score : null;
  }
  return scores;
};

const responseErrorMessage = async (
  response: Response,
  fallback: string,
): Promise<string> => {
  const payload = await response.json().catch(() => null);
  return payload?.error?.message || payload?.message || fallback;
};

export function useRiskAssessmentSurvey({
  artifactId,
  enabled,
  canAutoOpen,
  riskMethodType,
}: {
  artifactId: string | null;
  enabled: boolean;
  canAutoOpen: boolean;
  riskMethodType: RiskAssessmentSurveyMethod;
}) {
  const questions = getRiskAssessmentSurveyQuestions(riskMethodType);
  const [loadStatus, setLoadStatus] = useState<LoadStatus>("idle");
  const [loadRevision, setLoadRevision] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [storedScores, setStoredScores] =
    useState<RiskAssessmentSurveyScores | null>(null);
  const [storedFeedback, setStoredFeedback] = useState("");
  const [draftScores, setDraftScores] = useState<RiskAssessmentSurveyScores>(
    emptyRiskAssessmentSurveyScores,
  );
  const [draftFeedback, setDraftFeedback] = useState("");
  const [promptReady, setPromptReady] = useState(false);
  const [promptDelayMs, setPromptDelayMs] = useState(
    RISK_ASSESSMENT_SURVEY_DELAY_MS,
  );
  const [autoPromptAttempt, setAutoPromptAttempt] = useState(0);
  const [autoPromptExhausted, setAutoPromptExhausted] = useState(false);
  const [loadRetryDismissed, setLoadRetryDismissed] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [openReason, setOpenReason] = useState<OpenReason>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveNotice, setSaveNotice] = useState<string | null>(null);

  useEffect(() => {
    setLoadStatus("idle");
    setSubmitted(false);
    setStoredScores(null);
    setStoredFeedback("");
    setDraftScores(emptyRiskAssessmentSurveyScores());
    setDraftFeedback("");
    setPromptReady(false);
    setPromptDelayMs(RISK_ASSESSMENT_SURVEY_DELAY_MS);
    setAutoPromptAttempt(0);
    setAutoPromptExhausted(false);
    setLoadRetryDismissed(false);
    setIsOpen(false);
    setOpenReason(null);
    setIsSaving(false);
    setSaveError(null);
    setSaveNotice(null);
  }, [artifactId]);

  useEffect(() => {
    if (!enabled || !artifactId) return;
    const controller = new AbortController();
    setLoadStatus("loading");

    const load = async () => {
      try {
        const apiUrl =
          process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000";
        const response = await fetchWithAuth(
          `${apiUrl}/api/artifacts/${encodeURIComponent(artifactId)}/risk-assessment-survey`,
          {
            credentials: "include",
            skipRedirectOn401: false,
            signal: controller.signal,
          },
        );
        if (!response.ok) {
          throw new Error(
            await responseErrorMessage(
              response,
              "설문 상태를 불러오지 못했습니다.",
            ),
          );
        }
        const data = unwrapApiData<SurveyResponse>(await response.json());
        const scores = normalizedResponseScores(data?.scores ?? null);
        const hasStoredSurvey =
          data?.submitted === true && isCompleteRiskAssessmentSurvey(scores);
        setSubmitted(hasStoredSurvey);
        setStoredScores(hasStoredSurvey ? scores : null);
        setStoredFeedback(
          typeof data?.additionalFeedback === "string"
            ? data.additionalFeedback
            : "",
        );
        setLoadStatus("ready");
      } catch (error) {
        if (controller.signal.aborted) return;
        console.warn("[RiskAssessmentSurvey] load failed:", error);
        setLoadStatus("error");
      }
    };

    void load();
    return () => controller.abort();
  }, [artifactId, enabled, loadRevision]);

  useEffect(() => {
    if (
      !enabled ||
      !artifactId ||
      promptReady ||
      submitted ||
      autoPromptExhausted
    ) {
      return;
    }
    let remainingMs = promptDelayMs;
    let visibleSince: number | null = null;
    let timer: number | null = null;

    const pause = () => {
      if (visibleSince !== null) {
        remainingMs = Math.max(
          0,
          remainingMs - (performance.now() - visibleSince),
        );
        visibleSince = null;
      }
      if (timer !== null) {
        window.clearTimeout(timer);
        timer = null;
      }
    };

    const resume = () => {
      pause();
      if (document.visibilityState !== "visible") return;
      if (remainingMs <= 0) {
        setPromptReady(true);
        return;
      }
      visibleSince = performance.now();
      timer = window.setTimeout(() => {
        timer = null;
        visibleSince = null;
        remainingMs = 0;
        setPromptReady(true);
      }, remainingMs);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") resume();
      else pause();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    resume();
    return () => {
      pause();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [
    artifactId,
    autoPromptExhausted,
    enabled,
    promptDelayMs,
    promptReady,
    submitted,
  ]);

  const openForm = useCallback(
    (reason: Exclude<OpenReason, null>) => {
      setDraftScores(
        storedScores ? { ...storedScores } : emptyRiskAssessmentSurveyScores(),
      );
      setDraftFeedback(storedFeedback);
      setSaveError(null);
      setOpenReason(reason);
      setIsOpen(true);
    },
    [storedFeedback, storedScores],
  );

  useEffect(() => {
    if (
      promptReady &&
      loadStatus === "ready" &&
      !submitted &&
      !autoPromptExhausted &&
      canAutoOpen &&
      !isOpen
    ) {
      openForm("auto");
    }
  }, [
    canAutoOpen,
    isOpen,
    loadStatus,
    openForm,
    promptReady,
    submitted,
  ]);

  const close = useCallback(() => {
    setIsOpen(false);
    setOpenReason(null);
    setSaveError(null);
    if (submitted || openReason !== "auto") return;

    const nextPrompt = getRiskAssessmentSurveyReprompt(autoPromptAttempt);
    setPromptReady(false);
    if (!nextPrompt) {
      setAutoPromptExhausted(true);
      return;
    }
    setAutoPromptAttempt(nextPrompt.attempt);
    setPromptDelayMs(nextPrompt.delayMs);
  }, [autoPromptAttempt, openReason, submitted]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [close, isOpen]);

  const updateScore = useCallback(
    (key: RiskAssessmentSurveyScoreKey, value: number) => {
      setDraftScores((previous) => ({ ...previous, [key]: value }));
      setSaveError(null);
      setSaveNotice(null);
    },
    [],
  );

  const save = useCallback(async () => {
    if (
      !artifactId ||
      isSaving ||
      !isCompleteRiskAssessmentSurvey(draftScores)
    ) {
      return;
    }
    setIsSaving(true);
    setSaveError(null);
    try {
      const apiUrl = process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000";
      const response = await fetchWithAuth(
        `${apiUrl}/api/artifacts/${encodeURIComponent(artifactId)}/risk-assessment-survey`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          skipRedirectOn401: false,
          body: JSON.stringify({
            ...draftScores,
            additionalFeedback: draftFeedback.trim() || null,
          }),
        },
      );
      if (!response.ok) {
        throw new Error(
          await responseErrorMessage(response, "설문을 저장하지 못했습니다."),
        );
      }
      const data = unwrapApiData<SurveyResponse>(await response.json());
      const savedScores = normalizedResponseScores(data?.scores ?? draftScores);
      setStoredScores(savedScores);
      setStoredFeedback(
        typeof data?.additionalFeedback === "string"
          ? data.additionalFeedback
          : "",
      );
      setDraftScores(savedScores);
      setDraftFeedback(
        typeof data?.additionalFeedback === "string"
          ? data.additionalFeedback
          : "",
      );
      setSubmitted(true);
      setSaveNotice(
        openReason === "edit"
          ? "평가가 수정되었습니다."
          : "평가가 저장되었습니다. 감사합니다.",
      );
      setIsOpen(false);
      setOpenReason(null);
    } catch (error) {
      console.error("[RiskAssessmentSurvey] save failed:", error);
      setSaveError(
        error instanceof Error
          ? error.message
          : "설문을 저장하지 못했습니다. 잠시 후 다시 시도해 주세요.",
      );
    } finally {
      setIsSaving(false);
    }
  }, [artifactId, draftFeedback, draftScores, isSaving, openReason]);

  return {
    questions,
    loadStatus,
    submitted,
    draftScores,
    draftFeedback,
    setDraftFeedback: (value: string) => {
      setDraftFeedback(value);
      setSaveError(null);
      setSaveNotice(null);
    },
    promptReady,
    autoPromptAttempt,
    isOpen,
    openReason,
    isSaving,
    saveError,
    saveNotice,
    canSubmit: isCompleteRiskAssessmentSurvey(draftScores) && !isSaving,
    canClose:
      openReason !== "auto" ||
      autoPromptAttempt < RISK_ASSESSMENT_SURVEY_REPROMPT_DELAYS_MS.length,
    introAttempt: openReason === "auto" ? autoPromptAttempt : 0,
    shouldShowLoadRetry:
      enabled && promptReady && loadStatus === "error" && !loadRetryDismissed,
    openForEdit: () => openForm("edit"),
    updateScore,
    close,
    dismissLoadRetry: () => setLoadRetryDismissed(true),
    retryLoad: () => {
      setLoadRetryDismissed(false);
      setLoadRevision((previous) => previous + 1);
    },
    save,
  };
}

export type RiskAssessmentSurveyController = ReturnType<
  typeof useRiskAssessmentSurvey
>;

export function RiskAssessmentSurveyPanel({
  survey,
  widthClassName,
}: {
  survey: RiskAssessmentSurveyController;
  widthClassName: string;
}) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [gradientVisible, setGradientVisible] = useState(false);
  const [visibleIntroCharacters, setVisibleIntroCharacters] = useState(0);
  const [cardVisible, setCardVisible] = useState(false);
  const stepCount = survey.questions.length + 1;
  const introLines =
    SURVEY_INTRO_VARIANTS[survey.introAttempt] ??
    SURVEY_INTRO_VARIANTS[SURVEY_INTRO_VARIANTS.length - 1];
  const introCharacterCount = getSurveyIntroCharacterCount(introLines);
  const introReveal = getSurveyIntroReveal(visibleIntroCharacters, introLines);
  const isIntroTyping =
    visibleIntroCharacters > 0 && visibleIntroCharacters < introCharacterCount;

  useEffect(() => {
    let typingStartTimer: number | null = null;
    let typingTimer: number | null = null;
    let cardRevealTimer: number | null = null;
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const shouldAnimate = !prefersReducedMotion;

    const entranceFrame = window.requestAnimationFrame(() => {
      setGradientVisible(true);
      if (!shouldAnimate) {
        setVisibleIntroCharacters(introCharacterCount);
        setCardVisible(true);
        return;
      }

      typingStartTimer = window.setTimeout(() => {
        let revealedCharacters = 0;
        typingTimer = window.setInterval(() => {
          revealedCharacters = Math.min(
            introCharacterCount,
            revealedCharacters + SURVEY_TYPING_CHARACTERS_PER_FRAME,
          );
          setVisibleIntroCharacters(revealedCharacters);
          if (revealedCharacters < introCharacterCount) return;

          if (typingTimer !== null) window.clearInterval(typingTimer);
          typingTimer = null;
          cardRevealTimer = window.setTimeout(
            () => setCardVisible(true),
            SURVEY_CARD_REVEAL_DELAY_MS,
          );
        }, SURVEY_TYPING_FRAME_MS);
      }, SURVEY_TYPING_START_DELAY_MS);
    });

    return () => {
      window.cancelAnimationFrame(entranceFrame);
      if (typingStartTimer !== null) window.clearTimeout(typingStartTimer);
      if (typingTimer !== null) window.clearInterval(typingTimer);
      if (cardRevealTimer !== null) window.clearTimeout(cardRevealTimer);
    };
  }, [introCharacterCount, survey.openReason]);

  useEffect(() => {
    if (cardVisible) panelRef.current?.focus();
  }, [cardVisible]);

  const isFeedbackStep = currentStep === survey.questions.length;
  const currentQuestion = isFeedbackStep
    ? null
    : survey.questions[currentStep];
  const canMoveNext = currentQuestion
    ? survey.draftScores[currentQuestion.key] !== null
    : false;

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!isFeedbackStep) {
      if (canMoveNext) setCurrentStep((previous) => previous + 1);
      return;
    }
    void survey.save();
  };

  return (
    <div
      className={`pointer-events-none absolute inset-x-0 bottom-0 z-40 bg-gradient-to-t from-background via-background via-55% to-transparent px-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-72 transition-opacity duration-200 ease-out motion-reduce:transition-none ${gradientVisible ? "opacity-100" : "opacity-0"}`}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-labelledby="risk-assessment-survey-title"
        aria-describedby="risk-assessment-survey-description"
        aria-live={survey.openReason === "auto" ? "polite" : undefined}
        tabIndex={-1}
        className={`mx-auto w-full outline-none ${widthClassName}`}
      >
        <div className="mb-5 bg-background px-2 text-center">
          <strong id="risk-assessment-survey-title" className="sr-only">
            {SURVEY_INTRO_TITLE}
          </strong>
          <p id="risk-assessment-survey-description" className="sr-only">
            {introLines.join(" ")}
          </p>
          <div aria-hidden="true">
            <strong className="block text-[18px] font-black text-gray-950 dark:text-gray-100">
              <span>{introReveal.title}</span>
              {isIntroTyping &&
                introReveal.title.length > 0 &&
                visibleIntroCharacters <= SURVEY_INTRO_TITLE.length && (
                  <span className="ml-0.5 inline-block h-[1em] w-px translate-y-[2px] animate-pulse bg-current" />
                )}
              <span className="invisible">
                {SURVEY_INTRO_TITLE.slice(introReveal.title.length)}
              </span>
            </strong>
            <div className="mt-2.5 text-[13px] font-semibold leading-[1.6] text-gray-600 dark:text-gray-300">
              {introLines.map((line, index) => {
                const visibleLine = introReveal.lines[index] ?? "";
                return (
                  <span
                    key={line}
                    className={index === 0 ? "block" : "mt-1 block"}
                  >
                    <span>{visibleLine}</span>
                    {isIntroTyping &&
                      visibleLine.length > 0 &&
                      visibleIntroCharacters >
                        SURVEY_INTRO_TITLE.length +
                          introLines
                            .slice(0, index)
                            .reduce(
                              (total, previousLine) =>
                                total + previousLine.length,
                              0,
                            ) &&
                      visibleIntroCharacters <=
                        SURVEY_INTRO_TITLE.length +
                          introLines
                            .slice(0, index + 1)
                            .reduce(
                              (total, previousLine) =>
                                total + previousLine.length,
                              0,
                            ) && (
                        <span className="ml-0.5 inline-block h-[1em] w-px translate-y-[2px] animate-pulse bg-current" />
                      )}
                    <span className="invisible">
                      {line.slice(visibleLine.length)}
                    </span>
                  </span>
                );
              })}
            </div>
          </div>
        </div>

        <div
          aria-hidden={!cardVisible}
          inert={!cardVisible ? true : undefined}
          className={`relative origin-bottom transform-gpu overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-[0_8px_40px_rgba(0,0,0,0.14)] backdrop-blur-3xl dark:border-[#3a3a40] dark:bg-[#1a1a1d] dark:shadow-[0_8px_40px_rgba(0,0,0,0.45)] ${cardVisible ? "risk-assessment-survey-card-enter pointer-events-auto translate-y-0 scale-100 opacity-100" : "pointer-events-none translate-y-10 scale-[0.94] opacity-0"}`}
        >
          {survey.canClose && (
            <button
              type="button"
              onClick={survey.close}
              aria-label="설문 닫기"
              className="absolute right-3 top-2.5 z-10 flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-[#2a2a2e] dark:hover:text-gray-200"
            >
              <X size={16} aria-hidden="true" />
            </button>
          )}
          <form
            onSubmit={handleSubmit}
            className="flex h-[196px] flex-col sm:h-[204px]"
          >
            <div className="min-h-0 flex-1 py-2 pl-4 pr-12 sm:pl-5 sm:pr-14">
              {currentQuestion ? (
                <fieldset className="flex h-full min-w-0 flex-col justify-center">
                  <legend className="sr-only">{currentQuestion.label}</legend>
                  <span className="text-[10px] font-black text-seexr-primary">
                    문항 {currentStep + 1}/{stepCount}
                  </span>
                  <p className="mt-1 text-[14px] font-black leading-relaxed text-gray-900 dark:text-gray-100">
                    {currentQuestion.label}
                  </p>
                  <div className="mt-2 grid grid-cols-7 gap-1 sm:gap-1.5">
                    {LIKERT_OPTIONS.map((option) => {
                      const checked =
                        survey.draftScores[currentQuestion.key] ===
                        option.value;
                      return (
                        <label
                          key={option.value}
                          className={`flex h-12 cursor-pointer flex-col items-center justify-center rounded-xl border px-0.5 text-center transition-colors focus-within:ring-2 focus-within:ring-seexr-solid/40 ${checked ? "border-seexr-solid bg-seexr-solid text-white" : "border-gray-200 bg-gray-50 text-gray-500 hover:border-seexr-solid/50 hover:bg-seexr-solid/5 hover:text-seexr-primary dark:border-[#39393f] dark:bg-[#202023] dark:text-gray-300 dark:hover:border-seexr-solid/60"}`}
                        >
                          <input
                            type="radio"
                            name={`risk-survey-${currentQuestion.key}`}
                            value={option.value}
                            checked={checked}
                            onChange={() =>
                              survey.updateScore(
                                currentQuestion.key,
                                option.value,
                              )
                            }
                            aria-label={`${currentQuestion.label} — ${option.label} (${option.value})`}
                            className="sr-only"
                          />
                          <span className="text-[13px] font-black">
                            {option.value}
                          </span>
                          <span
                            className={`mt-0.5 text-[7px] font-semibold leading-[1.05] sm:text-[8px] ${checked ? "text-white/80" : "text-gray-400"}`}
                          >
                            {option.label}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </fieldset>
              ) : (
                <div className="flex h-full flex-col justify-center">
                  <span className="text-[10px] font-black text-seexr-primary">
                    문항 {currentStep + 1}/{stepCount}
                  </span>
                  <label
                    htmlFor="risk-assessment-survey-feedback"
                    className="mt-1 text-[13px] font-black leading-relaxed text-gray-900 dark:text-gray-100"
                  >
                    추가적으로 하고 싶은 말이 있다면 자유롭게 작성해 주세요.
                    <span className="ml-1 text-[10px] font-medium text-gray-400">
                      (선택)
                    </span>
                  </label>
                  <textarea
                    id="risk-assessment-survey-feedback"
                    value={survey.draftFeedback}
                    onChange={(event) =>
                      survey.setDraftFeedback(event.target.value)
                    }
                    maxLength={RISK_ASSESSMENT_SURVEY_FEEDBACK_MAX_LENGTH}
                    rows={2}
                    placeholder="개선되었으면 하는 점이나 유용했던 부분을 알려주세요."
                    className="mt-1.5 min-h-16 w-full resize-none rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-[12px] font-medium leading-relaxed text-gray-800 outline-none transition-colors placeholder:text-gray-400 focus:border-seexr-solid focus:ring-2 focus:ring-seexr-solid/10 dark:border-[#39393f] dark:bg-[#202023] dark:text-gray-100"
                  />
                  <span className="mt-1 flex items-center justify-between gap-3">
                    {survey.saveError ? (
                      <span
                        role="alert"
                        className="text-[10px] font-semibold text-red-600 dark:text-red-300"
                      >
                        {survey.saveError}
                      </span>
                    ) : (
                      <span />
                    )}
                    <span className="shrink-0 text-[9px] font-medium text-gray-400">
                      {survey.draftFeedback.length.toLocaleString("ko-KR")}/
                      {RISK_ASSESSMENT_SURVEY_FEEDBACK_MAX_LENGTH.toLocaleString(
                        "ko-KR",
                      )}
                    </span>
                  </span>
                </div>
              )}
            </div>

            <footer className="flex shrink-0 items-center gap-2 border-t border-gray-100 px-4 py-2 dark:border-[#2a2a2e] sm:px-5">
              <button
                type="button"
                disabled={currentStep === 0 || survey.isSaving}
                onClick={() =>
                  setCurrentStep((previous) => Math.max(0, previous - 1))
                }
                className="inline-flex h-8 min-w-16 items-center justify-center rounded-xl border border-gray-200 px-3 text-[10px] font-black text-gray-600 transition-colors hover:bg-gray-50 disabled:invisible dark:border-[#39393f] dark:text-gray-300 dark:hover:bg-[#242428]"
              >
                이전
              </button>
              <div
                className="flex min-w-0 flex-1 items-center justify-center gap-2"
                aria-label="설문 진행 표시"
              >
                <span className="flex items-center gap-1" aria-hidden="true">
                  {Array.from({ length: stepCount }, (_, index) => (
                    <span
                      key={index}
                      className={`h-1.5 rounded-full transition-all ${index === currentStep ? "w-5 bg-seexr-solid" : index < currentStep ? "w-1.5 bg-seexr-solid/45" : "w-1.5 bg-gray-200 dark:bg-[#3a3a40]"}`}
                    />
                  ))}
                </span>
              </div>
              <button
                type="submit"
                disabled={
                  survey.isSaving ||
                  (isFeedbackStep ? !survey.canSubmit : !canMoveNext)
                }
                className="inline-flex h-8 min-w-16 items-center justify-center gap-2 rounded-xl bg-seexr-solid px-3 text-[10px] font-black text-white transition-colors hover:bg-seexr-solid/90 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400 dark:disabled:bg-[#2a2a2e] dark:disabled:text-gray-500"
              >
                {survey.isSaving && (
                  <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current/30 border-t-current" />
                )}
                {survey.isSaving
                  ? "저장 중…"
                  : isFeedbackStep
                    ? survey.submitted
                      ? "평가 수정"
                      : "평가 제출"
                    : "다음"}
              </button>
            </footer>
          </form>
        </div>
      </div>
    </div>
  );
}

export function RiskAssessmentSurveyLoadRetry({
  survey,
  widthClassName,
}: {
  survey: RiskAssessmentSurveyController;
  widthClassName: string;
}) {
  if (!survey.shouldShowLoadRetry) return null;

  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-0 z-40 bg-gradient-to-t from-background via-background via-70% to-transparent px-6 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-12">
      <div className={`mx-auto w-full ${widthClassName}`}>
        <div className="pointer-events-auto flex items-center gap-3 rounded-2xl border border-amber-200 bg-white px-4 py-3 dark:border-amber-900/70 dark:bg-[#1a1a1d]">
          <MessageSquareText
            size={18}
            className="shrink-0 text-amber-500"
            aria-hidden="true"
          />
          <p className="min-w-0 flex-1 text-[11px] font-semibold leading-relaxed text-gray-600 dark:text-gray-300">
            설문 상태를 불러오지 못했습니다. 연결을 확인한 뒤 다시 시도해
            주세요.
          </p>
          <button
            type="button"
            onClick={survey.retryLoad}
            className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg bg-gray-900 px-3 text-[10px] font-black text-white dark:bg-white dark:text-gray-900"
          >
            <RotateCw size={12} aria-hidden="true" />
            재시도
          </button>
          <button
            type="button"
            onClick={survey.dismissLoadRetry}
            aria-label="설문 상태 안내 닫기"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-[#2a2a2e]"
          >
            <X size={15} aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}

export function RiskAssessmentSurveyEditEntry({
  survey,
}: {
  survey: RiskAssessmentSurveyController;
}) {
  if (!survey.submitted) return null;

  return (
    <section className="mt-4 rounded-2xl border border-gray-200 bg-white px-5 py-4 shadow-sm dark:border-[#2a2a2e] dark:bg-[#161618]">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <span className="flex min-w-0 flex-1 items-start gap-2.5">
          <CheckCircle2
            size={18}
            className="mt-0.5 shrink-0 text-seexr-primary"
            aria-hidden="true"
          />
          <span>
            <strong className="block text-[12px] font-black text-gray-900 dark:text-gray-100">
              위험성평가 품질 평가를 제출했습니다.
            </strong>
            <span
              role={survey.saveNotice ? "status" : undefined}
              className="mt-0.5 block text-[10px] font-medium leading-relaxed text-gray-500 dark:text-gray-400"
            >
              {survey.saveNotice ||
                "제출한 점수와 자유 의견은 언제든 다시 수정할 수 있습니다."}
            </span>
          </span>
        </span>
        <button
          type="button"
          onClick={survey.openForEdit}
          className="inline-flex h-9 shrink-0 items-center justify-center rounded-xl border border-gray-200 px-3.5 text-[11px] font-black text-gray-700 transition-colors hover:border-seexr-solid/40 hover:bg-seexr-solid/5 hover:text-seexr-primary dark:border-[#39393f] dark:text-gray-200"
        >
          제출한 평가 수정
        </button>
      </div>
    </section>
  );
}
